import { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Icon } from "@iconify/react";
import { formatK, formatNumber, niceCeil } from "../../utils/dashboardFormat";

const PERIODS = ["Yearly", "Monthly", "Weekly", "Today"];

const MessageAnalytics = ({ charts, summary, legacy }) => {
    const [period, setPeriod] = useState("Monthly");

    const volume = charts?.messageVolume?.[period] || null;

    const chartData = useMemo(() => {
        if (volume?.labels?.length) {
            return {
                labels: volume.labels,
                sent: volume.sent || [],
                failed: volume.failed || [],
                total: volume.total ?? (volume.sent || []).reduce((a, b) => a + (Number(b) || 0), 0),
                max: volume.max ?? Math.max(0, ...(volume.sent || []), ...(volume.failed || [])),
            };
        }

        // Legacy fallback: arrays without labels
        const legacyArr = legacy?.[period];
        if (Array.isArray(legacyArr) && legacyArr.length) {
            const labels = legacyArr.map((_, i) => String(i + 1));
            return {
                labels,
                sent: legacyArr.map((n) => Number(n) || 0),
                failed: [],
                total: legacyArr.reduce((a, b) => a + (Number(b) || 0), 0),
                max: Math.max(0, ...legacyArr.map((n) => Number(n) || 0)),
            };
        }

        return { labels: [], sent: [], failed: [], total: 0, max: 0 };
    }, [volume, legacy, period]);

    const hasData = chartData.labels.length > 0 && chartData.sent.some((n) => Number(n) > 0);
    const yMax = niceCeil(chartData.max);
    const currentHour = summary?.messages?.currentHourSent ?? 0;

    const series = [
        { name: "Sent", data: chartData.sent },
        ...(chartData.failed.some((n) => Number(n) > 0)
            ? [{ name: "Failed", data: chartData.failed }]
            : []),
    ];

    const options = {
        chart: {
            id: "message-analytics",
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: "inherit",
        },
        colors: ["#00a884", "#EF4444"],
        stroke: {
            curve: "smooth",
            width: [3, 2],
            dashArray: [0, 5],
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.35,
                opacityTo: 0.05,
                stops: [0, 90, 100],
            },
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: chartData.labels,
            labels: {
                style: { colors: "#667781", fontSize: "11px" },
                rotate: period === "Today" || period === "Weekly" ? -35 : 0,
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            min: 0,
            max: yMax,
            tickAmount: 4,
            labels: {
                formatter: (val) => formatK(val),
                style: { colors: "#667781", fontSize: "11px" },
            },
        },
        grid: {
            borderColor: "#eef1f4",
            strokeDashArray: 4,
        },
        legend: {
            show: series.length > 1,
            position: "top",
            horizontalAlign: "right",
        },
        tooltip: {
            y: {
                formatter: (val) => formatNumber(val),
            },
        },
    };

    return (
        <div className="col-xxl-8 col-lg-8 col-md-12">
            <div className="card h-100">
                <div className="card-body">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div>
                            <h6 className="text-lg mb-0">Message Analytics</h6>
                            <p className="mb-0 text-secondary-light text-sm">
                                Message volume over time
                            </p>
                        </div>
                        <select
                            className="form-select bg-base form-select-sm w-auto"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                        >
                            {PERIODS.map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="d-flex flex-wrap align-items-end gap-2 mt-3 mb-2">
                        <h3 className="mb-0 fw-bold">{formatNumber(chartData.total)}</h3>
                        <span className="text-secondary-light text-sm pb-1">
                            Current hour: {formatNumber(currentHour)}
                        </span>
                    </div>

                    {hasData ? (
                        <ReactApexChart
                            options={options}
                            series={series}
                            type="area"
                            height={280}
                        />
                    ) : (
                        <div className="dash-empty">
                            <Icon icon="mdi:chart-timeline-variant" />
                            <span>No message volume for this period</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessageAnalytics;
